"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader, GraduationCap, Globe, ArrowRight, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function StudentContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-lg bg-secondary/20 flex items-center justify-center">
          <CircleCheck className="size-10 text-secondary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold text-primary-foreground">Richiesta Inviata</h3>
          <p className="text-primary-foreground/70 max-w-md mx-auto font-sans">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 bg-[#FFC829] text-primary hover:bg-[#FFC829]/90 rounded-lg">
          Invia un'altra richiesta
        </Button>
      </div>;
  }
  return <div ref={formRef} className={`transition-all duration-1000 ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className="mb-10 space-y-3">
        <h2 className="text-3xl md:text-4xl font-default font-bold text-primary-foreground">
          Inizia il tuo percorso
        </h2>
        <p className="text-primary-foreground/70 font-sans text-base md:text-lg">
          Compila il modulo sottostante. I nostri consulenti accademici ti contatteranno entro 24 ore.
        </p>
      </div>

      <Form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="firstName" rules={{
          required: "Il nome è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[14px] font-normal text-primary-foreground/80">Nome</FormLabel>
                <FormControl>
                  <Input placeholder="Mario" {...field} className="bg-black/20 border-white/10 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary focus-visible:border-secondary rounded-sm h-12" />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>} />
          <FormField control={control} name="lastName" rules={{
          required: "Il cognome è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[14px] font-normal text-primary-foreground/80">Cognome</FormLabel>
                <FormControl>
                  <Input placeholder="Rossi" {...field} className="bg-black/20 border-white/10 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary focus-visible:border-secondary rounded-sm h-12" />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="email" rules={{
          required: "L'email è obbligatoria",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Indirizzo email non valido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[14px] font-normal text-primary-foreground/80">Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="mario.rossi@example.com" {...field} className="bg-black/20 border-white/10 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary focus-visible:border-secondary rounded-sm h-12" />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>} />
          <FormField control={control} name="phone" rules={{
          required: "Il numero di telefono è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[14px] font-normal text-primary-foreground/80">Telefono</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+39 333 1234567" {...field} className="bg-black/20 border-white/10 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary focus-visible:border-secondary rounded-sm h-12" />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>} />
        </div>

        <FormField control={control} name="destination" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[14px] font-normal text-primary-foreground/80">Destinazione di interesse (Opzionale)</FormLabel>
              <FormControl>
                <Input placeholder="Es. Stati Uniti, Regno Unito, Australia..." {...field} className="bg-black/20 border-white/10 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary focus-visible:border-secondary rounded-sm h-12" />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Il messaggio è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[14px] font-normal text-primary-foreground/80">Come possiamo aiutarti?</FormLabel>
              <FormControl>
                <Textarea placeholder="Descrivi i tuoi obiettivi accademici e le tue necessità..." {...field} className="bg-black/20 border-white/10 text-primary-foreground placeholder:text-primary-foreground/30 focus-visible:ring-secondary focus-visible:border-secondary rounded-sm min-h-[120px] resize-none" />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-sm">
            <p className="text-red-200 text-sm font-sans">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-[#FFC829] text-primary hover:bg-[#FFC829]/90 rounded-lg h-14 text-base font-semibold tracking-wide uppercase mt-4 transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,200,41,0.3)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Elaborazione in corso...
            </> : <>
              Richiedi Consulenza
              <ArrowRight className="ml-2 size-5" />
            </>}
        </Button>
        
        <p className="text-center text-primary-foreground/50 text-xs font-sans mt-4">
          Inviando questo modulo, accetti la nostra politica sulla privacy e il trattamento dei dati personali.
        </p>
      </Form>
    </div>;
}
export default function ContattaciStudenti() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="contattaci-studenti" className="bg-background w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full">
        
        {}
        <div className="relative flex flex-col justify-end p-8 lg:p-16 xl:p-24 min-h-[50vh] lg:min-h-screen overflow-hidden bg-primary">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663124/img/executive_university_campus_global.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663124/img/executive_university_campus_global.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663124/img/executive_university_campus_global.png-wvc-sizes" alt="Campus universitario internazionale" className="absolute inset-0 w-full h-full object-cover object-center" loading="lazy" />
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/80 to-primary/20 mix-blend-multiply" />
          <div className="absolute inset-0 bg-primary/40" />
          
          <div ref={textRef} className={`relative z-10 space-y-8 max-w-xl transition-all duration-1000 ${isTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-sm px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border-none">
                <Globe className="size-3.5 mr-1.5" />
                Mobilità Internazionale
              </Badge>
              <Badge className="bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm rounded-sm px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-white/20">
                <GraduationCap className="size-3.5 mr-1.5" />
                Eccellenza Accademica
              </Badge>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-white leading-[1.1] tracking-tight">
              Il Tuo Futuro Accademico Globale Inizia Qui.
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 font-sans font-light leading-relaxed max-w-lg">
              Affidati ai nostri esperti per guidarti attraverso ogni fase del tuo percorso di studi internazionale. Dalla scelta dell'istituto ideale all'ottenimento del visto studentesco.
            </p>
            
            <div className="pt-4 flex items-center gap-6 text-white/60 font-sans text-sm">
              <div className="flex items-center gap-2">
                <div className="size-1.5 rounded-lg bg-secondary" />
                <span>Consulenza Personalizzata</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="size-1.5 rounded-lg bg-secondary" />
                <span>Supporto Visti 100%</span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="flex flex-col justify-center p-8 lg:p-16 xl:p-24 bg-primary">
          <FormProvider formId="student-contact-form" sectionName="Contattaci Studenti" defaultValues={{
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          destination: "",
          message: ""
        }} successMessage="Grazie per averci contattato. Un nostro consulente accademico ti risponderà al più presto per organizzare un colloquio conoscitivo.">
            <StudentContactForm />
          </FormProvider>
        </div>
        
      </div>
    </section>;
}